/**
 * WordArt: Publisher-style text styles (fill / outline / effects combinations), text *transforms*
 * (follow-path and warp shapes) and OpenType typography helpers (stylistic sets, swash, alternates, ligatures).
 *
 * Text-box properties written by this module:
 *   color, textFill, textOutline, textEffects           (the "WordArt style")
 *   wordArt: { styleId, transform }                     (transform id from WORDART_TRANSFORMS, 'none' = plain text box)
 *   ligatures: true|false|'none'|'standard'|'standard-contextual'|'historical-discretionary'|'all'
 *   stylisticSet: 0..20, swash: bool, stylisticAlternates: bool
 */
import { tint, shade, SCHEME_PRESETS } from './colors';
import { measureWidth, measureInk } from './textLayout';

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const solid = (hex) => ({ type: 'solid', color: hex });
const grad = (c1, c2, angle = 180) => ({ type: 'gradient', gradient: { type: 'linear', angle, stops: [{ color: c1, position: 0 }, { color: c2, position: 100 }] } });
const pattern = (pat, fg, bg) => ({ type: 'pattern', pattern: pat, color: fg, background: bg });
const outline = (hex, width) => ({ color: hex, width });
const NO_OUTLINE = { color: '#000000', width: 0 };
const NO_FX = { shadow: null, glow: null, reflection: null, bevel: null };

const shadow = (color = 'rgba(0,0,0,0.55)', hard = false) => ({ preset: hard ? 'hard-br' : 'outer-br', dx: hard ? 3 : 2, dy: hard ? 3 : 2, blur: hard ? 0 : 3, color });
const glow = (color, size = 8) => ({ preset: `glow-${size}`, size, color, opacity: 0.75 });
const reflection = () => ({ preset: 'half-0', visible: 0.55, offset: 0, opacity: 0.4 });
const bevel = (preset, strength = 0.6) => ({ preset, hl: 1, sh: 1, strength });
const fx = (o) => ({ ...NO_FX, ...o });

const fillColorOf = (fill) => {
  if (!fill) return '#000000';
  if (fill.type === 'solid') return fill.color;
  if (fill.type === 'gradient') return fill.gradient?.stops?.[0]?.color || '#000000';
  if (fill.type === 'pattern') return fill.color || '#000000';
  return '#000000';
};

/**
 * The 20 WordArt styles of the Text Box tab, built from the active colour scheme
 * (Text 1 = s[0], Text 2 = s[1], Accent 1-5 = s[2..6], Background 1 = s[7]).
 */
export const wordArtStyles = (schemeId = 'office') => {
  const s = (SCHEME_PRESETS[schemeId] || SCHEME_PRESETS.office).map((c) => c.hex);
  const [t1, t2, a1, a2, a3, a4, a5, bg1] = s;
  const list = [
    // Row 1
    { id: 'fill-t1-shadow', name: 'Fill – Text 1, Shadow', fill: solid(t1), outline: NO_OUTLINE, effects: fx({ shadow: shadow() }) },
    { id: 'fill-a1-shadow', name: 'Fill – Accent 1, Shadow', fill: solid(a1), outline: NO_OUTLINE, effects: fx({ shadow: shadow() }) },
    { id: 'fill-a2-outline-a2', name: 'Fill – Accent 2 (light), Outline – Accent 2', fill: solid(tint(a2, 0.35)), outline: outline(shade(a2, 0.3), 1), effects: NO_FX },
    { id: 'fill-bg1-outline-a1-shadow', name: 'Fill – Background 1, Outline – Accent 1, Shadow', fill: solid(bg1), outline: outline(a1, 1), effects: fx({ shadow: shadow() }) },
    { id: 'fill-a4-soft-bevel', name: 'Fill – Accent 4, Soft Bevel', fill: solid(a4), outline: NO_OUTLINE, effects: fx({ bevel: bevel('soft-round', 0.7) }) },
    // Row 2
    { id: 'gradient-gray', name: 'Gradient Fill – Gray', fill: grad(tint(t1, 0.6), tint(t1, 0.15)), outline: NO_OUTLINE, effects: NO_FX },
    { id: 'gradient-a1-reflection', name: 'Gradient Fill – Accent 1, Reflection', fill: grad(tint(a1, 0.35), a1), outline: NO_OUTLINE, effects: fx({ reflection: reflection() }) },
    { id: 'gradient-a4-outline-a4', name: 'Gradient Fill – Accent 4, Outline – Accent 4', fill: grad(tint(a4, 0.45), a4), outline: outline(shade(a4, 0.3), 0.75), effects: NO_FX },
    { id: 'fill-bg1-outline-a1-glow-a1', name: 'Fill – Background 1, Outline – Accent 1, Glow – Accent 1', fill: solid(bg1), outline: outline(a1, 1), effects: fx({ glow: glow(a1, 8) }) },
    { id: 'fill-a5-sharp-bevel', name: 'Fill – Accent 5, Sharp Bevel', fill: solid(a5), outline: NO_OUTLINE, effects: fx({ bevel: bevel('hard-edge', 0.8) }) },
    // Row 3
    { id: 'fill-t1-outline-bg1-hard-shadow', name: 'Fill – Text 1, Outline – Background 1, Hard Shadow', fill: solid(t1), outline: outline(bg1, 1), effects: fx({ shadow: shadow(tint(t1, 0.55), true) }) },
    { id: 'fill-a1-outline-bg1-hard-shadow-a1', name: 'Fill – Accent 1, Outline – Background 1, Hard Shadow – Accent 1', fill: solid(a1), outline: outline(bg1, 1), effects: fx({ shadow: shadow(shade(a1, 0.45), true) }) },
    { id: 'fill-a2-outline-a2-hard-shadow-a2', name: 'Fill – Accent 2, Outline – Accent 2, Hard Shadow – Accent 2', fill: solid(tint(a2, 0.2)), outline: outline(shade(a2, 0.35), 0.75), effects: fx({ shadow: shadow(shade(a2, 0.5), true) }) },
    { id: 'pattern-bg1-t2-shadow', name: 'Pattern Fill – Background 1, Text 2 Dark Upward Diagonal, Shadow', fill: pattern('stripes', t2, bg1), outline: outline(t2, 0.5), effects: fx({ shadow: shadow() }) },
    { id: 'fill-bg1-outline-a2-shadow', name: 'Fill – Background 1, Outline – Accent 2, Shadow', fill: solid(bg1), outline: outline(a2, 1.5), effects: fx({ shadow: shadow() }) },
    // Row 4
    { id: 'fill-a2-glow-a2', name: 'Fill – Accent 2, Glow – Accent 2', fill: solid(a2), outline: NO_OUTLINE, effects: fx({ glow: glow(a2, 8) }) },
    { id: 'gradient-a5-reflection', name: 'Gradient Fill – Accent 5, Reflection', fill: grad(a5, shade(a5, 0.5)), outline: NO_OUTLINE, effects: fx({ reflection: reflection() }) },
    { id: 'fill-t2-outline-a3-bevel', name: 'Fill – Text 2, Outline – Accent 3, Bevel', fill: solid(t2), outline: outline(a3, 1), effects: fx({ bevel: bevel('circle', 0.6), shadow: shadow() }) },
    { id: 'gradient-a1-shadow', name: 'Gradient Fill – Accent 1 (dark), Shadow', fill: grad(tint(a1, 0.2), shade(a1, 0.45), 90), outline: NO_OUTLINE, effects: fx({ shadow: shadow('rgba(0,0,0,0.45)') }) },
    { id: 'fill-a4-outline-bg1-glow-a4', name: 'Fill – Accent 4, Outline – Background 1, Glow – Accent 4', fill: solid(a4), outline: outline(bg1, 1), effects: fx({ glow: glow(a4, 10) }) },
  ];
  return list.map((st) => ({
    id: st.id,
    name: st.name,
    props: { color: fillColorOf(st.fill), textFill: st.fill, textOutline: st.outline, textEffects: st.effects, wordArt: { styleId: st.id } },
  }));
};

/** Props that remove any WordArt style (keeps the transform untouched). */
export const CLEAR_WORDART_PROPS = { textFill: null, textOutline: NO_OUTLINE, textEffects: NO_FX, color: '#000000' };

/** Find the style whose fill/outline/effects match the given props (used for the gallery's selected state). */
export const matchWordArtStyle = (p, schemeId) => {
  const list = wordArtStyles(schemeId);
  const key = (x) => JSON.stringify([x.textFill ?? null, x.textOutline?.width ? { color: x.textOutline.color, width: x.textOutline.width } : null, x.textEffects ?? null]);
  const k = key(p || {});
  return list.find((st) => key(st.props) === k)?.id || null;
};

/* ------------------------------------------------------------------ */
/* Transforms                                                          */
/* ------------------------------------------------------------------ */

const S = Math.sin;
const PI = Math.PI;
const tri = (u) => Math.abs(2 * u - 1); // 1 at the edges, 0 in the middle

/**
 * Envelope transforms return [top, bottom] as fractions (0..1) of the frame height for a glyph
 * whose horizontal centre is at u (0..1). Constant (bottom - top) keeps the glyph height; varying it stretches glyphs.
 */
export const WORDART_TRANSFORMS = [
  { id: 'none', name: 'No Transform', group: 'Plain' },
  // Follow Path
  { id: 'arch-up', name: 'Arch Up', group: 'Follow Path', kind: 'arc', from: 180, to: 0, sweep: 180 },
  { id: 'arch-down', name: 'Arch Down', group: 'Follow Path', kind: 'arc', from: 180, to: 360, sweep: 180 },
  { id: 'circle', name: 'Circle', group: 'Follow Path', kind: 'arc', from: 180, to: -180, sweep: 360 },
  // Warp
  { id: 'triangle-up', name: 'Triangle', group: 'Warp', kind: 'envelope', env: (u) => [0.55 * tri(u), 1] },
  { id: 'triangle-down', name: 'Triangle Inverted', group: 'Warp', kind: 'envelope', env: (u) => [0, 1 - 0.55 * tri(u)] },
  { id: 'chevron-up', name: 'Chevron Up', group: 'Warp', kind: 'envelope', env: (u) => [0.35 * tri(u), 0.65 + 0.35 * tri(u)] },
  { id: 'chevron-down', name: 'Chevron Down', group: 'Warp', kind: 'envelope', env: (u) => [0.35 * (1 - tri(u)), 0.65 + 0.35 * (1 - tri(u))] },
  { id: 'wave-1', name: 'Wave 1', group: 'Warp', kind: 'envelope', env: (u) => { const t = 0.18 + 0.18 * S(2 * PI * u); return [t, t + 0.64]; } },
  { id: 'wave-2', name: 'Wave 2', group: 'Warp', kind: 'envelope', env: (u) => { const t = 0.18 - 0.18 * S(2 * PI * u); return [t, t + 0.64]; } },
  { id: 'double-wave-1', name: 'Double Wave 1', group: 'Warp', kind: 'envelope', env: (u) => { const t = 0.15 + 0.15 * S(4 * PI * u); return [t, t + 0.7]; } },
  { id: 'double-wave-2', name: 'Double Wave 2', group: 'Warp', kind: 'envelope', env: (u) => { const t = 0.15 - 0.15 * S(4 * PI * u); return [t, t + 0.7]; } },
  { id: 'inflate', name: 'Inflate', group: 'Warp', kind: 'envelope', env: (u) => [0.25 * (1 - S(PI * u)), 1 - 0.25 * (1 - S(PI * u))] },
  { id: 'deflate', name: 'Deflate', group: 'Warp', kind: 'envelope', env: (u) => [0.25 * S(PI * u), 1 - 0.25 * S(PI * u)] },
  { id: 'inflate-bottom', name: 'Inflate Bottom', group: 'Warp', kind: 'envelope', env: (u) => [0, 0.65 + 0.35 * S(PI * u)] },
  { id: 'deflate-bottom', name: 'Deflate Bottom', group: 'Warp', kind: 'envelope', env: (u) => [0, 1 - 0.35 * S(PI * u)] },
  { id: 'inflate-top', name: 'Inflate Top', group: 'Warp', kind: 'envelope', env: (u) => [0.35 * (1 - S(PI * u)), 1] },
  { id: 'deflate-top', name: 'Deflate Top', group: 'Warp', kind: 'envelope', env: (u) => [0.35 * S(PI * u), 1] },
  { id: 'fade-right', name: 'Fade Right', group: 'Warp', kind: 'envelope', env: (u) => [0.35 * u, 1 - 0.35 * u] },
  { id: 'fade-left', name: 'Fade Left', group: 'Warp', kind: 'envelope', env: (u) => [0.35 * (1 - u), 1 - 0.35 * (1 - u)] },
  { id: 'fade-up', name: 'Fade Up', group: 'Warp', kind: 'envelope', env: () => [0, 1], css3d: 'perspective(600px) rotateX(38deg)' },
  { id: 'fade-down', name: 'Fade Down', group: 'Warp', kind: 'envelope', env: () => [0, 1], css3d: 'perspective(600px) rotateX(-38deg)' },
  { id: 'slant-up', name: 'Slant Up', group: 'Warp', kind: 'envelope', env: (u) => [0.4 * (1 - u), 0.6 + 0.4 * (1 - u)] },
  { id: 'slant-down', name: 'Slant Down', group: 'Warp', kind: 'envelope', env: (u) => [0.4 * u, 0.6 + 0.4 * u] },
  { id: 'cascade-up', name: 'Cascade Up', group: 'Warp', kind: 'envelope', env: (u) => [0.5 * (1 - u), 1] },
  { id: 'cascade-down', name: 'Cascade Down', group: 'Warp', kind: 'envelope', env: (u) => [0.5 * u, 1] },
  { id: 'can-up', name: 'Can Up', group: 'Warp', kind: 'envelope', env: (u) => { const t = 0.25 * (1 - S(PI * u)); return [t, t + 0.75]; } },
  { id: 'can-down', name: 'Can Down', group: 'Warp', kind: 'envelope', env: (u) => { const t = 0.25 * S(PI * u); return [t, t + 0.75]; } },
];

export const getTransform = (id) => WORDART_TRANSFORMS.find((t) => t.id === id) || WORDART_TRANSFORMS[0];
export const hasWordArtTransform = (p) => !!p?.wordArt?.transform && p.wordArt.transform !== 'none';

const NOMINAL = 100; // glyphs are laid out at 100px and the SVG is stretched to the frame

const measureStyleOf = (p) => ({ fontFamily: p.fontFamily || 'Inter', fontSize: NOMINAL, fontWeight: p.fontWeight, fontStyle: p.fontStyle, smallCaps: p.fontVariant === 'small-caps', letterSpacing: 0 });

/**
 * Lay out text for a WordArt transform.
 * Returns { glyphs: [{ ch, x, y, sy, rotate }], viewBox: [x, y, w, h], css3d } in nominal units
 * (font size NOMINAL). Glyphs are anchored at their baseline centre (text-anchor: middle).
 */
export const layoutWordArt = (text, p, transformId) => {
  const t = getTransform(transformId);
  const style = measureStyleOf(p);
  const lines = String(text || '').replace(/\r/g, '').split('\n').filter((l, i, a) => l.length || a.length === 1);
  if (!lines.length || !lines.join('').trim()) return { glyphs: [], viewBox: [0, 0, 100, 100], css3d: t.css3d };
  const glyphsPerLine = lines.map((line) => {
    const chars = Array.from(line);
    const adv = chars.map((ch) => measureWidth(ch, style));
    const total = adv.reduce((a, b) => a + b, 0);
    let x = 0;
    const centres = adv.map((w) => { const c = x + w / 2; x += w; return c; });
    const ink = measureInk(line.trim() || 'M', style); // real ink extents so the frame is filled edge to edge
    return { chars, adv, centres, total, asc: ink.ascent, desc: ink.descent };
  });
  const gap = NOMINAL * 0.12; // vertical gap between lines

  if (t.kind === 'arc') {
    // Every line follows its own concentric arc; the first line is the outermost.
    const glyphs = [];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const bump = (x, y) => { minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); };
    const longest = Math.max(...glyphsPerLine.map((g) => g.total), 1);
    const maxChars = Math.max(...glyphsPerLine.map((g) => g.chars.filter((c) => c !== ' ').length), 1);
    // Short texts sit on a shallower arc (a 3-letter word on a full half circle reads badly), circles always close.
    const sweepDeg = t.sweep === 360 ? 360 : Math.min(t.sweep, Math.max(70, maxChars * 22));
    const R0 = Math.max(longest / ((sweepDeg * PI) / 180), NOMINAL * 0.8);
    const downwards = t.id === 'arch-down';
    let R = R0;
    glyphsPerLine.forEach((g, li) => {
      if (li > 0) R = Math.max(NOMINAL * 0.6, R - (glyphsPerLine[li - 1].desc + g.asc + gap));
      const usable = t.sweep === 360 ? 1 : Math.min(1, g.total / longest); // shorter lines take a proportional sweep, centred
      const sweep = (sweepDeg * PI / 180) * usable;
      const startDeg = ((t.from + t.to) / 2 - (t.to > t.from ? 1 : -1) * sweepDeg / 2) * PI / 180; // centred on the arc's apex
      const dir = t.to > t.from ? 1 : -1;
      const centreShift = ((sweepDeg * PI / 180) - sweep) / 2;
      g.chars.forEach((ch, i) => {
        if (ch === ' ') return;
        const frac = g.total ? g.centres[i] / g.total : 0.5;
        const theta = startDeg + dir * (centreShift + frac * sweep);
        const cx = R * Math.cos(theta);
        const cy = -R * Math.sin(theta);
        // outward normal: away from the centre for arch-up/circle, towards the centre for arch-down
        const nx = Math.cos(theta) * (downwards ? -1 : 1);
        const ny = -Math.sin(theta) * (downwards ? -1 : 1);
        const rotate = downwards ? (270 - (theta * 180) / PI) : (90 - (theta * 180) / PI);
        glyphs.push({ ch, x: cx, y: cy, sy: 1, rotate: ((rotate + 540) % 360) - 180, w: g.adv[i] });
        bump(cx + nx * g.asc, cy + ny * g.asc);
        bump(cx - nx * g.desc, cy - ny * g.desc);
        // tangent extents so wide glyphs are not clipped
        const tx = -Math.sin(theta) * g.adv[i] / 2, ty = -Math.cos(theta) * g.adv[i] / 2;
        bump(cx + tx + nx * g.asc, cy + ty + ny * g.asc); bump(cx - tx + nx * g.asc, cy - ty + ny * g.asc);
      });
    });
    if (!glyphs.length) return { glyphs: [], viewBox: [0, 0, 100, 100], css3d: t.css3d };
    const pad = NOMINAL * 0.12;
    return { glyphs, viewBox: [minX - pad, minY - pad, maxX - minX + pad * 2, maxY - minY + pad * 2], css3d: t.css3d };
  }

  // Envelope: the ink block of all lines (width W, height H) is mapped column by column into [top(u), bottom(u)].
  const W = Math.max(...glyphsPerLine.map((g) => g.total), 1);
  let cursor = 0;
  const lineTops = glyphsPerLine.map((g, li) => { const top = cursor; cursor += g.asc + g.desc + (li < glyphsPerLine.length - 1 ? gap : 0); return top; });
  const H = Math.max(1, cursor);
  const env = t.env || (() => [0, 1]);
  const align = p.textAlign || 'center';
  const glyphs = [];
  glyphsPerLine.forEach((g, li) => {
    const offset = align === 'left' ? 0 : align === 'right' ? W - g.total : (W - g.total) / 2;
    const baseline = lineTops[li] + g.asc; // baseline within the unwarped ink block
    g.chars.forEach((ch, i) => {
      if (ch === ' ') return;
      const x = offset + g.centres[i];
      const u = W ? x / W : 0.5;
      const [top, bottom] = env(u);
      const sy = Math.max(0.05, bottom - top);
      const y = H * top + sy * baseline; // scaling about the baseline maps the ink top/bottom onto the envelope
      glyphs.push({ ch, x, y, sy, rotate: 0, w: g.adv[i] });
    });
  });
  // Breathing room so outlines, glows and shadows are not clipped by the text box frame.
  const padX = W * 0.03 + NOMINAL * 0.08, padY = NOMINAL * 0.1;
  return { glyphs, viewBox: [-padX, -padY, W + padX * 2, H + padY * 2], css3d: t.css3d };
};

/* ------------------------------------------------------------------ */
/* OpenType typography                                                 */
/* ------------------------------------------------------------------ */

export const LIGATURE_MODES = [
  { id: 'none', name: 'No Ligatures' },
  { id: 'standard', name: 'Standard Only' },
  { id: 'standard-contextual', name: 'Standard and Contextual' },
  { id: 'historical-discretionary', name: 'Historical and Discretionary' },
  { id: 'all', name: 'All' },
];

/** Normalise the ligatures property (legacy boolean or mode id) to a mode id. */
export const ligatureMode = (v) => {
  if (v === false || v === 'none') return 'none';
  if (v === true || v === undefined || v === null || v === 'standard-contextual') return 'standard-contextual';
  return LIGATURE_MODES.some((m) => m.id === v) ? v : 'standard-contextual';
};

export const ligaturesCss = (v) => {
  switch (ligatureMode(v)) {
    case 'none': return 'none';
    case 'standard': return 'common-ligatures no-contextual no-discretionary-ligatures no-historical-ligatures';
    case 'historical-discretionary': return 'common-ligatures historical-ligatures discretionary-ligatures';
    case 'all': return 'common-ligatures contextual historical-ligatures discretionary-ligatures';
    default: return 'common-ligatures contextual';
  }
};

export const STYLISTIC_SET_COUNT = 20;
export const ssTag = (n) => `ss${String(n).padStart(2, '0')}`;

/** CSS font-feature-settings for stylistic set / swash / stylistic alternates (undefined when none). */
export const fontFeatureSettings = (p = {}) => {
  const f = [];
  if (p.stylisticSet) f.push(`"${ssTag(p.stylisticSet)}" 1`);
  if (p.swash) f.push('"swsh" 1');
  if (p.stylisticAlternates) f.push('"salt" 1');
  return f.length ? f.join(', ') : undefined;
};
